import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CollapsibleDataCard from '../CollapsibleDataCard';
import { Button } from 'components/button';
import { theme } from 'styles/theme';
import ReconciliationModal from './ReconciliationModal';
import AppliedPaymentsScanModal from './AppliedPaymentsScanModal';
import { getAppliedPaymentsSummary, AppliedPaymentsSummary } from 'services/reconciliationService';

const AppliedPaymentsReconciliationCard: React.FC = () => {
  const [summary, setSummary] = useState<AppliedPaymentsSummary>({ invoicesToScan: 0, mismatches: 0, lastRun: null });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getAppliedPaymentsSummary();
        if (mounted) setSummary(data);
      } catch (e) {
        // noop
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <CollapsibleDataCard
      title="Applied Payments Reconciliation"
      totalCount={summary.invoicesToScan}
      summaryInfo={[
        { label: 'Invoices to scan', value: summary.invoicesToScan ?? '—', type: 'info' },
        { label: 'Mismatches found', value: summary.mismatches ?? '—', type: 'warning' },
        { label: 'Last run', value: summary.lastRun ?? '—', type: 'info' },
      ]}
    >
      <Body>
        <InfoNote>
          This tool verifies that payments applied to invoices in EServ are also applied correctly in QuickBooks Online. It focuses on items already synced to QBO (payment and invoice each have a QBO ID).
        </InfoNote>

        <Actions>
          <StyledButton primary size="small" onClick={() => setIsModalOpen(true)} title="Run incremental scan">Run Scan</StyledButton>
          <StyledButton size="small" disabled title="Backfill mismatches (coming soon)">Backfill</StyledButton>
          <Note>Coming soon — backfill and bulk scan.</Note>
        </Actions>

        <Hint>
          Implementation note: we will use sparse updates for payment applications in QBO to avoid overwriting existing links.
        </Hint>
      </Body>

      <ReconciliationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Applied Payments Scan"
      >
        <AppliedPaymentsScanModal />
      </ReconciliationModal>
    </CollapsibleDataCard>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoNote = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledButton = styled(Button)`
  height: 32px;
  min-height: 32px;
`;

const Note = styled.span`
  font-size: 12px;
  color: ${theme.color.subtext};
`;

const Hint = styled.div`
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  padding: 10px;
  border-radius: 6px;
  font-size: 12px;
`;

export default AppliedPaymentsReconciliationCard;

